import { Center, Loader, Stack, Text } from '@mantine/core'
import { ReactNode, Suspense } from 'react'
import { Typography } from '../../../../common/typography'

export function WidgetTitle({ children }: { children: ReactNode }) {
  return (
    <Typography
      fs="xxxl"
      fw={700}
      lh="75"
      level={2}
      fc={{ light: 'gray-3', dark: 'gray-3' }}
    >
      {children}
    </Typography>
  )
}

export function WidgetEmpty({ message }: { message: string }) {
  return (
    <Center w="100%" h="100%">
      <Text size="sm" c="dimmed">
        {message}
      </Text>
    </Center>
  )
}

export function WidgetSuspense({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <Center w="100%" h="100%">
          <Loader />
        </Center>
      }
    >
      {children}
    </Suspense>
  )
}

/**
 * Layout shell that mirrors the hard-coded TV dashboard's structure:
 * - title above the content (outside any background)
 * - content area optionally wrapped in a gray-9 padded card (like the
 *   original `.progressSection` / `.graphSection`)
 *
 * Pass `boxed={false}` for widgets where the content is bare on the chrome
 * background (e.g. ring grid in the original layout).
 *
 * `padding` matches the original sections: 'xl' for progress/table
 * sections, '24px 24px 24px 64px' for the bar chart's graph section.
 */
export function WidgetSection({
  title,
  boxed = true,
  padding = 'xl',
  children,
}: {
  title?: string
  boxed?: boolean
  padding?: string
  children: ReactNode
}) {
  return (
    <Stack gap="xs" h="100%" w="100%" style={{ overflow: 'hidden' }}>
      {title && <WidgetTitle>{title}</WidgetTitle>}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          ...(boxed
            ? {
                backgroundColor: 'var(--mantine-color-gray-9)',
                borderRadius: 'var(--mantine-radius-sm)',
                padding: padding.includes(' ')
                  ? padding
                  : `var(--mantine-spacing-${padding})`,
                boxSizing: 'border-box' as const,
                overflow: 'hidden',
              }
            : {}),
        }}
      >
        {children}
      </div>
    </Stack>
  )
}
